// PRE RMA CSB — Saisie vocale du registre de consultation (RCE), ligne par ligne.
//
// Fonctions PURES (aucun accès micro, DOM, réseau, IndexedDB) : testables seules. Le composant React ne fait que brancher le micro
// (Web Speech API) sur `appliquer(...)` et afficher l'état. Les règles métier ne sont PAS réimplémentées ici : une ligne dictée est
// contrôlée par `validerConsultation` (mêmes 31 règles que la saisie manuelle), `controlerRegistre` et `controlerDateSaisie`.
//
// Principes (cahier des charges) :
//   - l'agent lit les VALEURS d'une ligne du registre, de gauche à droite, sans dire le nom des champs ;
//   - une colonne = une valeur : une case vide se dit « vide » (explicite) — JAMAIS de valeur vide convertie en 0 / « Non » ;
//   - une valeur douteuse est marquée « À vérifier » et BLOQUE la progression : rien n'est décalé vers la colonne suivante ;
//   - rien n'est corrigé en silence (un âge « 3 ans » n'est pas réécrit « 36 mois » sans l'accord de l'agent) ;
//   - « Validé » ne valide que si la ligne est complète et sans erreur bloquante ; un silence ne valide jamais ;
//   - aucune écriture ici : l'enregistrement passe par la file d'attente existante (importerConsultations), en un seul lot.
import { DIAGNOSTICS_REF, RULES } from './constants.ts';
import { parseAge } from './age.ts';
import { validerConsultation } from './validation.ts';
import type { ConsultationInput } from './validation.ts';
import { controlerRegistre, numerosRce, registreDeAge } from './registre.ts';
import type { Registre } from './registre.ts';
import { controlerDateSaisie } from './semaines.ts';
import { normaliser, convertirDiagnostic, normaliserAge } from './importConsultations.ts';

// ===================================================================================================================
// 1. MAPPING colonnes du registre → champs du formulaire PRE RMA
// ===================================================================================================================
export type IdColonne =
  | 'rce' | 'fievre' | 'tdr' | 'age' | 'genre' | 'diag1' | 'diag2' | 'diag3' | 'diag4'
  | 'traumatisme' | 'traitement' | 'indicateur' | 'admission';
export type TypeColonne = 'rce' | 'ouinon' | 'tdr' | 'age' | 'genre' | 'diagnostic' | 'traumatisme' | 'traitement' | 'indicateur' | 'admission';

export interface ColonneVocale {
  id: IdColonne;
  libelle: string;
  /** Champ de ConsultationInput alimenté (même champ que le formulaire de saisie manuelle). */
  champ: string;
  type: TypeColonne;
  exemple: string;
}

/**
 * Colonnes de la saisie RCE, dans l'ORDRE PAR DÉFAUT = celui déjà utilisé par l'import de consultations du dépôt
 * (importConsultations.ts › depuisCellules : N° RCE, Fièvre, TDR, Âge, Genre, Diagnostic 1-4, Traumatisme, Traitement palu, Indicateur CRENAS, Admission)
 * et par le formulaire SaisieUnique.tsx. ⚠ Cet ordre n'a pas pu être comparé au registre papier réel (aucune copie dans le dépôt) :
 * l'agent peut le modifier dans l'écran (voir `validerOrdre`).
 */
export const COLONNES_CONSULTATION: readonly ColonneVocale[] = [
  { id: 'rce', libelle: 'N° RCE', champ: 'numeroRceBrut', type: 'rce', exemple: '« douze » · « douze virgule treize » · « CRENAS »' },
  { id: 'fievre', libelle: 'Cas de fièvre', champ: 'casFievre', type: 'ouinon', exemple: '« oui » · « non » · « vide »' },
  { id: 'tdr', libelle: 'Résultat TDR', champ: 'tdrResultat', type: 'tdr', exemple: '« cent un » · « 110 » · « vide »' },
  { id: 'age', libelle: 'Âge', champ: 'ageBrut', type: 'age', exemple: '« quinze jours » · « douze mois » · « dix ans »' },
  { id: 'genre', libelle: 'Genre', champ: 'genre', type: 'genre', exemple: '« masculin » · « féminin »' },
  { id: 'diag1', libelle: 'Diagnostic 1', champ: 'diagnostics[0]', type: 'diagnostic', exemple: '« paludisme simple »' },
  { id: 'diag2', libelle: 'Diagnostic 2', champ: 'diagnostics[1]', type: 'diagnostic', exemple: '« toux ou rhume » · « vide »' },
  { id: 'diag3', libelle: 'Diagnostic 3', champ: 'diagnostics[2]', type: 'diagnostic', exemple: '« vide »' },
  { id: 'diag4', libelle: 'Diagnostic 4', champ: 'diagnostics[3]', type: 'diagnostic', exemple: '« vide »' },
  { id: 'traumatisme', libelle: 'Type de traumatisme', champ: 'typeTraumatisme', type: 'traumatisme', exemple: '« violence sexuelle » · « aucun »' },
  { id: 'traitement', libelle: 'Traitement paludisme', champ: 'traitementPalu', type: 'traitement', exemple: '« ACT » · « artésunate injectable »' },
  { id: 'indicateur', libelle: 'Indicateur CRENAS', champ: 'indicateurCrenas', type: 'indicateur', exemple: '« MAM PB » · « MAS PT, œdème »' },
  { id: 'admission', libelle: 'Admission CRENAS', champ: 'admissionCrenas', type: 'admission', exemple: '« admis NA M » · « guéri »' },
];
export const ORDRE_PAR_DEFAUT: readonly IdColonne[] = COLONNES_CONSULTATION.map((c) => c.id);
const COLONNE = new Map<IdColonne, ColonneVocale>(COLONNES_CONSULTATION.map((c) => [c.id, c]));
export const colonneDe = (id: IdColonne): ColonneVocale => COLONNE.get(id)!;

/** Un ordre personnalisé doit contenir chaque colonne exactement une fois (sinon ordre par défaut + message). */
export function validerOrdre(ordre: unknown): { ok: true; ordre: IdColonne[] } | { ok: false; raison: string } {
  if (!Array.isArray(ordre)) return { ok: false, raison: 'Ordre des colonnes absent.' };
  const ids = ordre as unknown[];
  if (ids.length !== ORDRE_PAR_DEFAUT.length) return { ok: false, raison: `L'ordre doit contenir exactement ${ORDRE_PAR_DEFAUT.length} colonnes.` };
  if (new Set(ids).size !== ids.length) return { ok: false, raison: 'Une colonne apparaît deux fois.' };
  if (!ids.every((x) => typeof x === 'string' && COLONNE.has(x as IdColonne))) return { ok: false, raison: 'Colonne inconnue dans l’ordre.' };
  return { ok: true, ordre: ids as IdColonne[] };
}

// ===================================================================================================================
// 2. NOMBRES ET DATES PARLÉS (français ; malgache 0-10 uniquement, NON vérifié sur le terrain)
// ===================================================================================================================
const UNITES: Record<string, number> = { zero: 0, un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, six: 6, sept: 7, huit: 8, neuf: 9 };
const DIX_SEIZE: Record<string, number> = { dix: 10, onze: 11, douze: 12, treize: 13, quatorze: 14, quinze: 15, seize: 16 };
const DIZAINES: Record<string, number> = { vingt: 20, trente: 30, quarante: 40, cinquante: 50, soixante: 60, qv: 80 };
/** Chiffres malgaches 0-10 (aotra, iray, roa, telo, efatra, dimy, enina, fito, valo, sivy, folo) : mots isolés seulement. */
const MALGACHE: Record<string, number> = { aotra: 0, iray: 1, roa: 2, telo: 3, efatra: 4, dimy: 5, enina: 6, fito: 7, valo: 8, sivy: 9, folo: 10 };

function bloc99(brut: string[]): number | null {
  const t: string[] = [];
  for (let i = 0; i < brut.length; i++) {
    if (brut[i] === 'et') { // « vingt et un », « soixante et onze » : seul « et » légitime
      const avant = brut[i - 1], apres = brut[i + 1];
      if (avant && avant in DIZAINES && avant !== 'qv' && (apres === 'un' || apres === 'une' || apres === 'onze')) continue;
      return null;
    }
    t.push(brut[i]);
  }
  if (t.length === 0) return null;
  const u = (x: string) => (x in UNITES ? UNITES[x] : null);
  if (t.length === 1) return u(t[0]) ?? DIX_SEIZE[t[0]] ?? DIZAINES[t[0]] ?? null;
  if (t.length === 2) {
    const [a, b] = t;
    if (a === 'dix' && (b === 'sept' || b === 'huit' || b === 'neuf')) return 10 + UNITES[b];
    if (a in DIZAINES) {
      const base = DIZAINES[a];
      const ub = u(b);
      if (ub !== null && ub >= 1) return base + ub;
      if ((base === 60 || base === 80) && b in DIX_SEIZE) return base + DIX_SEIZE[b];
    }
    return null;
  }
  if (t.length === 3 && (t[0] === 'soixante' || t[0] === 'qv') && t[1] === 'dix' && (t[2] === 'sept' || t[2] === 'huit' || t[2] === 'neuf')) {
    return DIZAINES[t[0]] + 10 + UNITES[t[2]];
  }
  return null;
}

/** Mots/chiffres → entier 0…9999, ou null si la suite n'est PAS un nombre unique bien formé (« douze treize » → null : jamais additionné). */
export function nombreDepuisMots(tokensBruts: string[]): number | null {
  if (tokensBruts.length === 0) return null;
  if (tokensBruts.length === 1 && /^\d{1,9}$/.test(tokensBruts[0])) return Number(tokensBruts[0]);
  if (tokensBruts.length === 1 && tokensBruts[0] in MALGACHE) return MALGACHE[tokensBruts[0]];
  if (tokensBruts.some((x) => /\d/.test(x))) return null; // mélange chiffres + mots : ambigu
  const t: string[] = [];
  for (let i = 0; i < tokensBruts.length; i++) {
    const x = tokensBruts[i] === 'vingts' ? 'vingt' : tokensBruts[i] === 'cents' ? 'cent' : tokensBruts[i];
    if (x === 'quatre' && (tokensBruts[i + 1] === 'vingt' || tokensBruts[i + 1] === 'vingts')) { t.push('qv'); i++; } else t.push(x);
  }
  let reste = t;
  let milliers = 0;
  const iMille = reste.indexOf('mille');
  if (iMille >= 0) {
    const g = reste.slice(0, iMille);
    const m = g.length === 0 ? 1 : (g.length === 1 && g[0] in UNITES && UNITES[g[0]] >= 1 ? UNITES[g[0]] : null);
    if (m === null) return null;
    milliers = m * 1000;
    reste = reste.slice(iMille + 1);
    if (reste.includes('mille')) return null;
  }
  let centaines = 0;
  const iCent = reste.indexOf('cent');
  if (iCent >= 0) {
    const g = reste.slice(0, iCent);
    const c = g.length === 0 ? 1 : (g.length === 1 && g[0] in UNITES && UNITES[g[0]] >= 2 ? UNITES[g[0]] : null);
    if (c === null) return null;
    centaines = c * 100;
    reste = reste.slice(iCent + 1);
    if (reste.includes('cent')) return null;
  }
  const fin = reste.length === 0 ? 0 : bloc99(reste);
  if (fin === null) return null;
  if (milliers === 0 && centaines === 0 && reste.length === 0) return null;
  return milliers + centaines + fin;
}

const tokensDe = (s: string): string[] => normaliser(s).split(' ').filter(Boolean);

/** Nombre entier ou décimal parlé (« douze virgule cinq » → 12.5). Utilisé par les futurs modules ; une valeur vide n'est jamais 0. */
export function parserNombreDecimal(texte: string): number | null {
  const brut = String(texte ?? '').trim().toLowerCase().replace(/(\d)[,.](\d)/g, '$1 virgule $2');
  const t = tokensDe(brut);
  if (t.length === 0) return null;
  const i = t.indexOf('virgule');
  if (i < 0) return nombreDepuisMots(t);
  const g = t.slice(0, i), d = t.slice(i + 1);
  const ent = nombreDepuisMots(g);
  if (ent === null || d.length === 0) return null;
  const tousChiffres = d.every((x) => /^\d$/.test(x) || (x in UNITES));
  const dec = tousChiffres ? d.map((x) => (/^\d$/.test(x) ? x : String(UNITES[x]))).join('') : /^\d+$/.test(d.join('')) ? d.join('') : String(nombreDepuisMots(d) ?? '');
  if (dec === '' || dec === 'null') return null;
  return Number(`${ent}.${dec}`);
}

const MOIS_PARLES = ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];
const dateValideISO = (a: number, m: number, j: number) => { const d = new Date(Date.UTC(a, m - 1, j)); return d.getUTCFullYear() === a && d.getUTCMonth() === m - 1 && d.getUTCDate() === j; };
const iso = (a: number, m: number, j: number) => `${String(a).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(j).padStart(2, '0')}`;

/**
 * Date dictée → AAAA-MM-JJ. Formes : « 12/10/2026 », « 12 10 2026 », « douze octobre deux mille vingt six », « douze octobre » (année = `anneeDefaut`).
 * Une date impossible (31 février) ou ambiguë renvoie un échec : rien n'est deviné.
 */
export function interpreterDate(texte: string, anneeDefaut?: number): { ok: true; date: string } | { ok: false; raison: string } {
  const t = tokensDe(texte);
  if (t.length === 0) return { ok: false, raison: 'Date vide.' };
  const iMois = t.findIndex((x) => MOIS_PARLES.includes(x));
  let j: number | null, m: number | null, a: number | null;
  if (iMois >= 0) {
    j = nombreDepuisMots(t.slice(0, iMois));
    m = iMois + 1 > 0 ? MOIS_PARLES.indexOf(t[iMois]) + 1 : null;
    const anT = t.slice(iMois + 1);
    a = anT.length ? nombreDepuisMots(anT) : (anneeDefaut ?? null);
  } else if (t.length === 3 && t.every((x) => /^\d+$/.test(x))) {
    [j, m, a] = t.map(Number);
  } else return { ok: false, raison: 'Format de date non reconnu (ex. « 12/10/2026 » ou « douze octobre »).' };
  if (j === null || m === null || a === null) return { ok: false, raison: 'Jour, mois ou année non compris.' };
  if (a < 100 && anneeDefaut !== undefined) return { ok: false, raison: `Année « ${a} » incomplète : dites l’année en entier.` };
  if (!dateValideISO(a, m, j)) return { ok: false, raison: `Date impossible : ${j}/${m}/${a}.` };
  return { ok: true, date: iso(a, m, j) };
}

// ===================================================================================================================
// 3. INTERPRÉTATION D'UNE VALEUR DICTÉE (une colonne à la fois)
// ===================================================================================================================
export type StatutCellule = 'ATTENDU' | 'RECONNU' | 'ABSENT' | 'A_VERIFIER' | 'ERREUR';
export type SourceCellule = 'VOIX' | 'CLAVIER' | 'MANUEL' | 'PROPOSITION' | 'COMMANDE';

export interface Cellule {
  colonne: IdColonne;
  statut: StatutCellule;
  /** Texte entendu / saisi, tel quel. */
  brut: string;
  /** Valeur normalisée destinée au champ du formulaire (code pour un diagnostic) ; null si ATTENDU/ABSENT/ERREUR. */
  valeur: string | null;
  message?: string;
  /** Valeur suggérée, jamais appliquée sans clic / commande de l'agent. */
  proposition?: string | null;
  candidats?: string[];
  source?: SourceCellule;
}

export interface Interpretation { statut: Exclude<StatutCellule, 'ATTENDU'>; valeur: string | null; message?: string; proposition?: string | null; candidats?: string[] }
const ok = (valeur: string): Interpretation => ({ statut: 'RECONNU', valeur });
const aVerifier = (message: string, proposition: string | null = null, candidats?: string[]): Interpretation => ({ statut: 'A_VERIFIER', valeur: null, message, proposition, ...(candidats ? { candidats } : {}) });
const erreur = (message: string): Interpretation => ({ statut: 'ERREUR', valeur: null, message });

const MOTS_VIDES = ['vide', 'rien', 'neant', 'foana', 'case vide', 'pas de valeur', 'aucune valeur'];
/** « vide » dit explicitement. (« aucun » n'est une absence que pour le traumatisme.) */
export const estAbsenceExplicite = (texte: string) => MOTS_VIDES.includes(normaliser(texte));

const sansEspaces = (s: string) => normaliser(s).replace(/ /g, '');
const TRAITEMENTS = ['ACT', 'Artesunate injectable', 'Autre', 'ACT, Artesunate injectable'] as const;
const TRAUMATISMES: Array<{ code: string; libelle: string; formes: string[] }> = [
  { code: 'VIOLENCE_SEXUELLE', libelle: 'Violence sexuelle', formes: ['violence sexuelle'] },
  { code: 'AUTRE_VIOLENCE', libelle: 'Autre cas violence', formes: ['autre cas violence', 'autre violence', 'autre cas de violence'] },
  { code: 'AUTRE_TRAUMATISME', libelle: 'Autre cas traumatisme', formes: ['autre cas traumatisme', 'autre traumatisme', 'autre cas de traumatisme'] },
];
const INDICATEURS: Array<{ cle: string; libelle: string }> = [
  { cle: 'mampt', libelle: 'MAM P/T' }, { cle: 'mampb', libelle: 'MAM PB' }, { cle: 'maspt', libelle: 'MAS P/T' }, { cle: 'maspb', libelle: 'MAS PB' }, { cle: 'oedeme', libelle: 'Œdème' },
];

export const libelleDiagnostic = (code: string) => DIAGNOSTICS_REF.find((d) => d.code === code)?.libelle ?? code;

function interpreterDiagnostic(texte: string): Interpretation {
  const code = convertirDiagnostic(texte);
  if (code) return ok(code);
  const n = normaliser(texte);
  if (n.length >= 4) {
    const trouves = DIAGNOSTICS_REF.filter((d) => !d.legacy && (normaliser(d.libelle).includes(n) || normaliser(d.code).replace(/ /g, '') === n.replace(/ /g, '')));
    if (trouves.length === 1) return aVerifier('Diagnostic incomplet : confirmez la proposition.', trouves[0].code);
    if (trouves.length > 1) return aVerifier('Plusieurs diagnostics correspondent : choisissez.', null, trouves.slice(0, 4).map((d) => d.code));
  }
  return aVerifier(`Diagnostic non reconnu : « ${texte.trim()} ».`);
}

/** Interprète UNE valeur dictée pour UN type de colonne. Ne devine jamais : le doute devient « À vérifier » (avec proposition éventuelle). */
export function interpreterValeur(type: TypeColonne, texteBrut: string): Interpretation {
  const texte = String(texteBrut ?? '').trim();
  if (texte === '') return erreur('Aucune valeur entendue.');
  const n = normaliser(texte);
  const t = n.split(' ').filter(Boolean);

  if (type === 'traumatisme' && (n === 'aucun' || n === 'aucune')) return { statut: 'ABSENT', valeur: null, message: 'Aucun traumatisme (dit explicitement).' };
  if (estAbsenceExplicite(texte)) return { statut: 'ABSENT', valeur: null, message: 'Case vide (dit explicitement).' };

  switch (type) {
    case 'rce': {
      if (sansEspaces(texte) === 'crenas') return ok('CRENAS');
      if (t.length === 0) return erreur('Aucune valeur entendue.');
      const morceaux = texte.toLowerCase().replace(/[;,]/g, ' virgule ').split(/\bvirgule\b/).map((x) => x.trim());
      if (morceaux.some((x) => x === '')) return aVerifier('N° RCE : virgule mal placée.');
      const nums: number[] = [];
      for (const m of morceaux) {
        const tk = tokensDe(m);
        if (tk.length > 1 && tk.every((x) => /^\d+$/.test(x))) return aVerifier('Plusieurs nombres sans « virgule » : dites « virgule » entre deux N° RCE (ex. « douze virgule treize »).');
        const v = nombreDepuisMots(tk);
        if (v === null) return aVerifier(`N° RCE non compris : « ${m} ». Pour plusieurs N°, dites « virgule » entre eux.`);
        if (v === 0) return aVerifier('N° RCE égal à 0 : à vérifier.');
        nums.push(v);
      }
      return ok(nums.join(', '));
    }
    case 'ouinon': {
      if (['oui', 'eny'].includes(n)) return ok('Oui');
      if (['non', 'tsia'].includes(n)) return ok('Non');
      return aVerifier(`« ${texte} » n’est ni « oui » ni « non ».`);
    }
    case 'tdr': {
      const digitsMots = t.length >= 2 && t.every((x) => /^\d$/.test(x) || (x in UNITES));
      const candidat = digitsMots ? t.map((x) => (/^\d$/.test(x) ? x : String(UNITES[x]))).join('') : String(nombreDepuisMots(t) ?? '');
      if (['100', '101', '110', '111'].includes(candidat)) return ok(candidat);
      if (['negatif', 'positif'].includes(n)) return aVerifier('« positif / négatif » ne suffit pas : dites le code TDR (100, 101, 110 ou 111).');
      return aVerifier(`Résultat TDR non reconnu : « ${texte} » (attendu : 100, 101, 110 ou 111).`);
    }
    case 'age': {
      const iU = t.length - 1;
      const u = t[iU];
      const unite = ['jour', 'jours'].includes(u) ? 'jours' : u === 'mois' ? 'mois' : ['an', 'ans'].includes(u) ? 'ans' : null;
      if (!unite) return erreur('Unité d’âge manquante : dites « jours », « mois » ou « ans ».');
      const v = nombreDepuisMots(t.slice(0, iU));
      if (v === null) return aVerifier(`Nombre de l’âge non compris : « ${texte} ».`);
      const direct = `${v} ${unite}`;
      if (parseAge(direct).ok) return ok(direct);
      const propose = normaliserAge(direct);
      if (propose !== direct && parseAge(propose).ok) return aVerifier(`« ${direct} » n’est pas accepté tel quel par le registre : l’application propose « ${propose} ».`, propose);
      return erreur(parseAge(direct).erreur ?? 'Âge invalide.');
    }
    case 'genre': {
      if (['m', 'em', 'masculin', 'garcon', 'homme', 'h', 'lehilahy'].includes(n)) return ok('M');
      if (['f', 'ef', 'effe', 'feminin', 'fille', 'femme', 'vavy'].includes(n)) return ok('F');
      return aVerifier(`Genre non reconnu : « ${texte} » (dites « masculin » ou « féminin »).`);
    }
    case 'diagnostic': return interpreterDiagnostic(texte);
    case 'traumatisme': {
      const trouve = TRAUMATISMES.find((x) => x.formes.includes(n) || normaliser(x.libelle) === n);
      if (trouve) return ok(trouve.code);
      if (n === 'autre') return aVerifier('« Autre » : violence ou traumatisme ?', null, TRAUMATISMES.slice(1).map((x) => x.code));
      return aVerifier(`Type de traumatisme non reconnu : « ${texte} ».`);
    }
    case 'traitement': {
      const cle = t.filter((x) => x !== 'et').join('');
      const trouve = TRAITEMENTS.find((x) => normaliser(x).replace(/ /g, '') === cle);
      if (trouve) return ok(trouve);
      if (cle === 'acte') return aVerifier('« acte » : voulez-vous dire ACT ?', 'ACT');
      return aVerifier(`Traitement non reconnu : « ${texte} ».`);
    }
    case 'indicateur': {
      let reste = sansEspaces(texte.replace(/\bet\b/gi, ' '));
      const trouves: string[] = [];
      for (const ind of INDICATEURS) if (reste.includes(ind.cle)) { trouves.push(ind.libelle); reste = reste.replace(ind.cle, ''); }
      if (trouves.length && reste === '') return ok(trouves.join(', '));
      return aVerifier(`Indicateur CRENAS non reconnu : « ${texte} ».`, trouves.length ? trouves.join(', ') : null);
    }
    case 'admission': {
      const trouve = RULES.crenas.admissions.find((x) => normaliser(x) === n);
      if (trouve) return ok(trouve);
      return aVerifier(`Admission CRENAS non reconnue : « ${texte} ».`);
    }
  }
}

/** Texte lisible d'une cellule (libellé du diagnostic, « (vide) », etc.). */
export function affichageCellule(c: Cellule): string {
  if (c.statut === 'ATTENDU') return '—';
  if (c.statut === 'ABSENT') return '(vide)';
  if (c.valeur === null) return c.brut || '—';
  const col = colonneDe(c.colonne);
  if (col.type === 'diagnostic') return libelleDiagnostic(c.valeur);
  if (col.type === 'traumatisme') return TRAUMATISMES.find((x) => x.code === c.valeur)?.libelle ?? c.valeur;
  if (col.type === 'genre') return c.valeur === 'M' ? 'M (masculin)' : 'F (féminin)';
  return c.valeur;
}

// ===================================================================================================================
// 4. COMMANDES VOCALES (énoncé ENTIER seulement : « valider » dans une phrase n'est pas une commande)
// ===================================================================================================================
export type Commande = 'COMMENCER' | 'PAUSE' | 'REPRENDRE' | 'REPETER' | 'CORRIGER' | 'ANNULER_LIGNE' | 'VERIFIER' | 'VALIDER' | 'TERMINER' | 'RESTE_VIDE';
const COMMANDES: Record<Commande, string[]> = {
  COMMENCER: ['commencer', 'commencer la saisie', 'demarrer'],
  PAUSE: ['pause', 'mettre en pause'],
  REPRENDRE: ['reprendre', 'continuer', 'reprise'],
  REPETER: ['repeter', 'repete', 'repeter la derniere valeur', 'repeter la valeur'],
  CORRIGER: ['corriger', 'correction', 'corriger la derniere valeur', 'corriger la valeur'],
  ANNULER_LIGNE: ['annuler la ligne', 'annuler ligne', 'effacer la ligne'],
  VERIFIER: ['verifier', 'verifier la ligne', 'verification'],
  VALIDER: ['valide', 'valider', 'valider la ligne', 'ligne validee'],
  TERMINER: ['terminer', 'terminer la saisie', 'fin de saisie'],
  RESTE_VIDE: ['fin de ligne', 'le reste est vide', 'reste vide', 'tout le reste est vide'],
};
export function detecterCommande(texte: string): Commande | null {
  const n = normaliser(texte);
  for (const [cmd, formes] of Object.entries(COMMANDES) as Array<[Commande, string[]]>) if (formes.includes(n)) return cmd;
  return null;
}

// ===================================================================================================================
// 5. ÉTAT : lignes, cellules, machine à états
// ===================================================================================================================
export type ModeSaisie = 'INACTIF' | 'ECOUTE' | 'PAUSE' | 'RECAP';
export interface LigneSaisie {
  /** Position de la ligne dans le registre papier (1 = première ligne dictée). Conservée avec la ligne jusqu'à l'enregistrement. */
  position: number;
  cellules: Cellule[];
  date: string;
  verifiee: boolean;
  validee: boolean;
}
export interface Message { niveau: 'info' | 'warn' | 'bad'; texte: string }
export interface EtatSaisie {
  version: 1;
  ordre: IdColonne[];
  mode: ModeSaisie;
  lignes: LigneSaisie[];
  /** Index de la ligne en cours de dictée. */
  courante: number;
  /** Cellule à re-dicter (commande « corriger »), sinon null : la prochaine valeur va à la première cellule non résolue. */
  cible: number | null;
  dateConsultation: string;
  message: Message | null;
  /** Texte à lire à voix haute (« répéter ») ; `n` change à chaque demande. */
  annonce: { n: number; texte: string } | null;
}

export interface ContexteSaisie {
  registre: Registre;
  periode: { annee: number; mois: number };
  /** AAAA-MM-JJ du jour (passé en paramètre : fonctions pures). */
  aujourdhui: string;
  /** Consultations déjà présentes pour la période (pour interdire les doublons). */
  existants: Array<{ numeroRceBrut: string; ageBrut?: string | null }>;
  /** En dessous de ce score (0-1) renvoyé par le moteur vocal, une valeur est « À vérifier ». */
  seuilConfiance?: number;
}
export const SEUIL_CONFIANCE_PAR_DEFAUT = 0.55;

const cellVide = (colonne: IdColonne): Cellule => ({ colonne, statut: 'ATTENDU', brut: '', valeur: null });
export function creerLigne(position: number, date: string, ordre: readonly IdColonne[]): LigneSaisie {
  return { position, cellules: ordre.map(cellVide), date, verifiee: false, validee: false };
}
export function creerSaisie(opts: { ordre?: readonly IdColonne[]; premierePosition?: number; dateConsultation?: string } = {}): EtatSaisie {
  const v = opts.ordre ? validerOrdre(opts.ordre) : { ok: true as const, ordre: [...ORDRE_PAR_DEFAUT] };
  const ordre = v.ok ? v.ordre : [...ORDRE_PAR_DEFAUT];
  const date = opts.dateConsultation ?? '';
  return {
    version: 1, ordre, mode: 'INACTIF', lignes: [creerLigne(Math.max(1, Math.floor(opts.premierePosition ?? 1)), date, ordre)],
    courante: 0, cible: null, dateConsultation: date, message: null, annonce: null,
  };
}

export const estResolue = (c: Cellule) => c.statut === 'RECONNU' || c.statut === 'ABSENT';
export const estSignalee = (c: Cellule) => c.statut === 'A_VERIFIER' || c.statut === 'ERREUR';
export const ligneVide = (l: LigneSaisie) => l.cellules.every((c) => c.statut === 'ATTENDU');
/** Première cellule non résolue (attendue OU signalée) ; -1 si la ligne est complète. */
export const curseur = (l: LigneSaisie) => l.cellules.findIndex((c) => !estResolue(c));

// ---- Analyse d'une ligne (validation réelle) -------------------------------------------------------------------
export interface ProblemeLigne { colonne?: IdColonne; message: string }
export interface AnalyseLigne {
  etat: 'INCOMPLETE' | 'A_CORRIGER' | 'PRETE';
  consultation: ConsultationInput | null;
  bloquants: ProblemeLigne[];
  /** Cellules encore attendues (obligatoires manquantes : toutes les colonnes doivent être dictées, ou dites « vide »). */
  manquantes: number;
  aVerifier: number;
  incoherences: number;
}

function colonneDuMessage(msg: string): IdColonne | undefined {
  if (/N° RCE/i.test(msg)) return 'rce';
  if (/âge/i.test(msg) && !/diagnostic|paludisme|MAS|MAM/i.test(msg)) return 'age';
  if (/genre|sexe/i.test(msg)) return 'genre';
  if (/TDR/i.test(msg)) return 'tdr';
  if (/Traitement/i.test(msg)) return 'traitement';
  if (/indicateur/i.test(msg)) return 'indicateur';
  if (/CRENAS/i.test(msg)) return 'admission';
  if (/diagnostic/i.test(msg) || /Paludisme|MAS|MAM/i.test(msg)) return 'diag1';
  return undefined;
}

const cellDe = (l: LigneSaisie, id: IdColonne) => l.cellules.find((c) => c.colonne === id)!;
const valeurDe = (l: LigneSaisie, id: IdColonne): string | null => { const c = cellDe(l, id); return c.statut === 'ABSENT' ? null : c.valeur; };

/** Clés de doublon « registre|numéro » d'une consultation. En « RCE Unique » : un seul registre. Sinon registre déduit de l'âge (jours). */
function clesDoublon(registre: Registre, numeroRceBrut: string, ageBrut: string | null | undefined): string[] {
  const nums = numerosRce(numeroRceBrut);
  if (nums.length === 0) return [];
  if (registre === 'UNIQUE') return nums.map((n) => `UNIQUE|${n}`);
  const a = parseAge(ageBrut ?? '');
  if (!a.ok || a.ageJours == null) return [];
  const r = registreDeAge(a.ageJours);
  return nums.map((n) => `${r}|${n}`);
}

export function analyserLigne(etat: EtatSaisie, index: number, ctx: ContexteSaisie): AnalyseLigne {
  const l = etat.lignes[index];
  const manquantes = l.cellules.filter((c) => c.statut === 'ATTENDU').length;
  const aVer = l.cellules.filter(estSignalee).length;
  if (aVer > 0 || manquantes > 0) {
    return { etat: aVer > 0 ? 'A_CORRIGER' : 'INCOMPLETE', consultation: null, bloquants: [], manquantes, aVerifier: aVer, incoherences: 0 };
  }
  const bloquants: ProblemeLigne[] = [];
  const diags = (['diag1', 'diag2', 'diag3', 'diag4'] as IdColonne[]).map((id) => valeurDe(l, id));
  const premierVide = diags.findIndex((d) => d === null);
  if (premierVide >= 0 && diags.slice(premierVide).some((d) => d !== null)) {
    bloquants.push({ colonne: (['diag1', 'diag2', 'diag3', 'diag4'] as IdColonne[])[premierVide], message: 'Un diagnostic suit une colonne « Diagnostic » vide : corrigez pour ne rien décaler.' });
  }
  const consultation: ConsultationInput = {
    numeroRceBrut: valeurDe(l, 'rce') ?? '',
    casFievre: valeurDe(l, 'fievre') as ConsultationInput['casFievre'],
    tdrResultat: valeurDe(l, 'tdr') as ConsultationInput['tdrResultat'],
    ageBrut: valeurDe(l, 'age'),
    genre: valeurDe(l, 'genre') as ConsultationInput['genre'],
    diagnostics: diags.filter((d): d is string => d !== null),
    typeTraumatisme: valeurDe(l, 'traumatisme'),
    traitementPalu: valeurDe(l, 'traitement'),
    indicateurCrenas: valeurDe(l, 'indicateur'),
    admissionCrenas: valeurDe(l, 'admission'),
    ...(l.date ? { dateConsultation: l.date } : {}),
  };
  if (consultation.numeroRceBrut === '') bloquants.push({ colonne: 'rce', message: 'N° RCE manquant : la ligne ne serait comptée nulle part.' });
  for (const e of validerConsultation(consultation).erreurs) bloquants.push({ colonne: colonneDuMessage(e), message: e });
  const regErr = controlerRegistre(ctx.registre, consultation.ageBrut);
  if (regErr) bloquants.push({ colonne: 'age', message: regErr });
  const dateErr = l.date ? controlerDateSaisie(l.date, ctx.periode, ctx.aujourdhui) : 'Date de consultation obligatoire.';
  if (dateErr) bloquants.push({ message: dateErr });

  // Doublons : contre l'existant ET contre les autres lignes dictées (tous registres confondus selon `registre`). CRENAS : jamais un doublon.
  const dejaPris = new Map<string, string>();
  for (const e of ctx.existants) for (const k of clesDoublon(ctx.registre, e.numeroRceBrut, e.ageBrut)) dejaPris.set(k, 'déjà enregistré dans cette période');
  etat.lignes.forEach((autre, j) => {
    if (j === index || ligneVide(autre)) return;
    const r = cellDe(autre, 'rce'), a = cellDe(autre, 'age');
    if (r.statut !== 'RECONNU' || !r.valeur) return;
    for (const k of clesDoublon(ctx.registre, r.valeur, a.statut === 'RECONNU' ? a.valeur : null)) if (!dejaPris.has(k)) dejaPris.set(k, `déjà dicté à la ligne ${autre.position} du registre`);
  });
  for (const k of clesDoublon(ctx.registre, consultation.numeroRceBrut, consultation.ageBrut)) {
    const ou = dejaPris.get(k);
    if (ou) bloquants.push({ colonne: 'rce', message: `N° RCE ${k.split('|')[1]} ${ou} (doublon refusé).` });
  }
  const nbIncoh = bloquants.filter((b) => b.message !== 'Date de consultation obligatoire.' && !/^La date|^Date invalide/.test(b.message)).length;
  return { etat: bloquants.length ? 'A_CORRIGER' : 'PRETE', consultation: bloquants.length ? null : consultation, bloquants, manquantes: 0, aVerifier: 0, incoherences: nbIncoh };
}

// ---- Récapitulatif général ------------------------------------------------------------------------------------
export interface Recapitulatif {
  lignesDictees: number;
  lignesVerifiees: number;
  lignesValidees: number;
  valeursACorriger: number;
  champsObligatoiresManquants: number;
  erreursCoherence: number;
  pretAEnregistrer: boolean;
  parLigne: Array<{ position: number; index: number; etat: AnalyseLigne['etat'] | 'VALIDEE'; validee: boolean; verifiee: boolean; bloquants: ProblemeLigne[] }>;
}
export function recapitulatif(etat: EtatSaisie, ctx: ContexteSaisie): Recapitulatif {
  const r: Recapitulatif = { lignesDictees: 0, lignesVerifiees: 0, lignesValidees: 0, valeursACorriger: 0, champsObligatoiresManquants: 0, erreursCoherence: 0, pretAEnregistrer: false, parLigne: [] };
  etat.lignes.forEach((l, index) => {
    if (ligneVide(l)) return;
    const a = analyserLigne(etat, index, ctx);
    r.lignesDictees++;
    if (l.verifiee || l.validee) r.lignesVerifiees++;
    if (l.validee && a.etat === 'PRETE') r.lignesValidees++;
    r.valeursACorriger += a.aVerifier;
    r.champsObligatoiresManquants += a.manquantes;
    r.erreursCoherence += a.incoherences;
    r.parLigne.push({ position: l.position, index, etat: l.validee && a.etat === 'PRETE' ? 'VALIDEE' : a.etat, validee: l.validee, verifiee: l.verifiee, bloquants: a.bloquants });
  });
  r.pretAEnregistrer = r.lignesDictees > 0 && r.lignesValidees === r.lignesDictees && r.valeursACorriger === 0 && r.champsObligatoiresManquants === 0 && r.erreursCoherence === 0;
  return r;
}

// ---- Actions ---------------------------------------------------------------------------------------------------
export type Action =
  | { type: 'COMMENCER' } | { type: 'PAUSE' } | { type: 'REPRENDRE' } | { type: 'REPETER' } | { type: 'CORRIGER' }
  | { type: 'ANNULER_LIGNE' } | { type: 'VERIFIER' } | { type: 'VALIDER' } | { type: 'TERMINER' } | { type: 'RESTE_VIDE' }
  | { type: 'TEXTE'; texte: string; confiance?: number; source?: SourceCellule }
  | { type: 'EDITER'; ligne: number; colonne: number; texte: string }
  | { type: 'VIDER_CELLULE'; ligne: number; colonne: number }
  | { type: 'ACCEPTER_PROPOSITION'; ligne: number; colonne: number }
  | { type: 'VALIDER_LIGNE'; ligne: number }
  | { type: 'DATE_LIGNE'; ligne: number; date: string }
  | { type: 'DATE_COURANTE'; date: string };

const COMMANDE_VERS_ACTION: Record<Commande, Action> = {
  COMMENCER: { type: 'COMMENCER' }, PAUSE: { type: 'PAUSE' }, REPRENDRE: { type: 'REPRENDRE' }, REPETER: { type: 'REPETER' }, CORRIGER: { type: 'CORRIGER' },
  ANNULER_LIGNE: { type: 'ANNULER_LIGNE' }, VERIFIER: { type: 'VERIFIER' }, VALIDER: { type: 'VALIDER' }, TERMINER: { type: 'TERMINER' }, RESTE_VIDE: { type: 'RESTE_VIDE' },
};

const msg = (niveau: Message['niveau'], texte: string): Message => ({ niveau, texte });
const avec = (e: EtatSaisie, p: Partial<EtatSaisie>): EtatSaisie => ({ ...e, ...p });
const avecLigne = (e: EtatSaisie, i: number, l: LigneSaisie): EtatSaisie => ({ ...e, lignes: e.lignes.map((x, j) => (j === i ? l : x)) });
const avecCellule = (l: LigneSaisie, k: number, c: Cellule): LigneSaisie => ({ ...l, cellules: l.cellules.map((x, j) => (j === k ? c : x)), verifiee: false, validee: false });

function cellDepuis(colonne: IdColonne, brut: string, it: Interpretation, source: SourceCellule): Cellule {
  return { colonne, statut: it.statut, brut, valeur: it.valeur, ...(it.message ? { message: it.message } : {}), ...(it.proposition ? { proposition: it.proposition } : {}), ...(it.candidats ? { candidats: it.candidats } : {}), source };
}

function nouvelleLigneSiBesoin(e: EtatSaisie): EtatSaisie {
  const derniere = e.lignes[e.lignes.length - 1];
  if (!ligneVide(derniere) || e.lignes.length === 0) {
    const l = creerLigne(derniere.position + 1, e.dateConsultation, e.ordre);
    return { ...e, lignes: [...e.lignes, l], courante: e.lignes.length };
  }
  return { ...e, courante: e.lignes.length - 1 };
}

/** Réducteur pur : (état, action, contexte) → nouvel état. Aucune action n'a d'effet de bord. */
export function appliquer(etat: EtatSaisie, action: Action, ctx: ContexteSaisie): EtatSaisie {
  const seuil = ctx.seuilConfiance ?? SEUIL_CONFIANCE_PAR_DEFAUT;
  const ligneC = etat.lignes[etat.courante];

  switch (action.type) {
    case 'COMMENCER': {
      if (etat.mode === 'ECOUTE') return avec(etat, { message: msg('info', 'La saisie est déjà en cours.') });
      return avec(nouvelleLigneSiBesoin(etat), { mode: 'ECOUTE', cible: null, message: msg('info', 'Écoute en cours : dites la valeur de la première colonne.') });
    }
    case 'PAUSE': return etat.mode === 'ECOUTE' ? avec(etat, { mode: 'PAUSE', message: msg('info', 'En pause. Dites « reprendre » ou appuyez sur Reprendre.') }) : etat;
    case 'REPRENDRE': {
      if (etat.mode === 'PAUSE') return avec(etat, { mode: 'ECOUTE', message: msg('info', 'Écoute en cours.') });
      if (etat.mode === 'RECAP') return avec(nouvelleLigneSiBesoin(etat), { mode: 'ECOUTE', cible: null, message: msg('info', 'Retour à la dictée.') });
      return etat;
    }
    case 'DATE_COURANTE': return { ...avecLigne(etat, etat.courante, { ...ligneC, date: action.date, verifiee: false, validee: false }), dateConsultation: action.date };
    case 'DATE_LIGNE': {
      const l = etat.lignes[action.ligne];
      return l ? avecLigne(etat, action.ligne, { ...l, date: action.date, verifiee: false, validee: false }) : etat;
    }
    case 'REPETER': {
      let k = -1;
      etat.lignes[etat.courante].cellules.forEach((c, j) => { if (c.statut !== 'ATTENDU') k = j; });
      if (k < 0) return avec(etat, { message: msg('info', 'Aucune valeur à répéter.') });
      const c = ligneC.cellules[k];
      const texte = `${colonneDe(c.colonne).libelle} : ${affichageCellule(c)}`;
      return avec(etat, { annonce: { n: (etat.annonce?.n ?? 0) + 1, texte }, message: msg('info', `Dernière valeur — ${texte}`) });
    }
    case 'CORRIGER': {
      const cur = curseur(ligneC);
      let k = cur >= 0 && estSignalee(ligneC.cellules[cur]) ? cur : -1;
      if (k < 0) ligneC.cellules.forEach((c, j) => { if (c.statut !== 'ATTENDU') k = j; });
      if (k < 0) return avec(etat, { message: msg('info', 'Aucune valeur à corriger.') });
      return avec(etat, { cible: k, message: msg('warn', `Dictez à nouveau : ${colonneDe(ligneC.cellules[k].colonne).libelle}.`) });
    }
    case 'ANNULER_LIGNE': {
      const vierge = creerLigne(ligneC.position, ligneC.date, etat.ordre);
      return { ...avecLigne(etat, etat.courante, vierge), cible: null, message: msg('warn', `Ligne ${ligneC.position} annulée : recommencez à la première colonne.`) };
    }
    case 'RESTE_VIDE': {
      if (etat.mode !== 'ECOUTE') return etat;
      const cur = curseur(ligneC);
      if (cur < 0) return avec(etat, { message: msg('info', 'La ligne est déjà complète.') });
      if (estSignalee(ligneC.cellules[cur])) return avec(etat, { message: msg('bad', 'Corrigez d’abord la valeur signalée.') });
      if (ligneC.cellules[cur].colonne === 'rce') return avec(etat, { message: msg('bad', 'Dictez d’abord le N° RCE.') });
      const cellules = ligneC.cellules.map((c, j) => (j >= cur && c.statut === 'ATTENDU' ? { ...c, statut: 'ABSENT' as const, brut: 'fin de ligne', valeur: null, message: 'Vide : « fin de ligne » dit par l’agent.', source: 'COMMANDE' as const } : c));
      return { ...avecLigne(etat, etat.courante, { ...ligneC, cellules, verifiee: false, validee: false }), message: msg('info', 'Colonnes restantes marquées « vide ». Vérifiez puis validez.') };
    }
    case 'VERIFIER': {
      const a = analyserLigne(etat, etat.courante, ctx);
      const base = avecLigne(etat, etat.courante, { ...ligneC, verifiee: true });
      if (a.etat === 'PRETE') return avec(base, { message: msg('info', 'Ligne prête : dites « validé » ou appuyez sur Valider cette ligne.') });
      return avec(base, { message: msg('bad', a.bloquants[0]?.message ?? (a.manquantes ? `${a.manquantes} colonne(s) encore à dicter.` : 'Des valeurs sont à vérifier.')) });
    }
    case 'VALIDER':
    case 'VALIDER_LIGNE': {
      const i = action.type === 'VALIDER' ? etat.courante : action.ligne;
      const l = etat.lignes[i];
      if (!l) return etat;
      if (action.type === 'VALIDER' && etat.mode !== 'ECOUTE') return avec(etat, { message: msg('warn', 'Validation vocale ignorée : la saisie n’est pas en écoute.') });
      const a = analyserLigne(etat, i, ctx);
      if (a.etat !== 'PRETE') {
        return avec(etat, { message: msg('bad', `Validation refusée : ${a.bloquants[0]?.message ?? (a.aVerifier ? 'valeur(s) à vérifier.' : `${a.manquantes} colonne(s) non dictée(s).`)}`) });
      }
      const validee = avecLigne(etat, i, { ...l, verifiee: true, validee: true });
      if (i !== etat.courante) return avec(validee, { message: msg('info', `Ligne ${l.position} validée.`) });
      const suivante = creerLigne(l.position + 1, etat.dateConsultation, etat.ordre);
      return { ...validee, lignes: [...validee.lignes, suivante], courante: validee.lignes.length, cible: null, message: msg('info', `Ligne ${l.position} validée. Ligne ${l.position + 1} : dites la valeur de la première colonne.`) };
    }
    case 'TERMINER': {
      if (!ligneVide(ligneC) && !ligneC.validee) return avec(etat, { message: msg('bad', `La ligne ${ligneC.position} n’est pas validée : validez-la ou dites « annuler la ligne » avant de terminer.`) });
      const lignes = etat.lignes.filter((l, j) => !(j === etat.lignes.length - 1 && ligneVide(l) && etat.lignes.length > 1));
      return { ...etat, lignes, courante: Math.max(0, lignes.length - 1), mode: 'RECAP', cible: null, message: msg('info', 'Saisie terminée : relisez le récapitulatif avant d’enregistrer.') };
    }
    case 'EDITER': {
      const l = etat.lignes[action.ligne];
      const c = l?.cellules[action.colonne];
      if (!l || !c) return etat;
      if (action.texte.trim() === '') return avecLigne(etat, action.ligne, avecCellule(l, action.colonne, cellVide(c.colonne)));
      const it = interpreterValeur(colonneDe(c.colonne).type, action.texte);
      return avecLigne(etat, action.ligne, avecCellule(l, action.colonne, cellDepuis(c.colonne, action.texte.trim(), it, 'MANUEL')));
    }
    case 'VIDER_CELLULE': {
      const l = etat.lignes[action.ligne];
      const c = l?.cellules[action.colonne];
      if (!l || !c) return etat;
      return avecLigne(etat, action.ligne, avecCellule(l, action.colonne, { colonne: c.colonne, statut: 'ABSENT', brut: '', valeur: null, message: 'Case vide choisie par l’agent.', source: 'MANUEL' }));
    }
    case 'ACCEPTER_PROPOSITION': {
      const l = etat.lignes[action.ligne];
      const c = l?.cellules[action.colonne];
      if (!l || !c || !c.proposition) return etat;
      const it = interpreterValeur(colonneDe(c.colonne).type, c.proposition);
      if (it.statut !== 'RECONNU') return avec(etat, { message: msg('bad', 'La proposition ne peut pas être acceptée telle quelle : corrigez la valeur.') });
      return avecLigne(etat, action.ligne, avecCellule(l, action.colonne, { ...cellDepuis(c.colonne, c.brut, it, 'PROPOSITION'), message: 'Proposition acceptée par l’agent.' }));
    }
    case 'TEXTE': {
      const texte = action.texte.trim();
      if (!texte) return etat;
      const cmd = detecterCommande(texte);
      if (cmd) return appliquer(etat, COMMANDE_VERS_ACTION[cmd], ctx);
      if (etat.mode === 'INACTIF') return avec(etat, { message: msg('info', 'Appuyez sur « Commencer » ou dites « commencer ».') });
      if (etat.mode === 'PAUSE') return avec(etat, { message: msg('info', 'En pause : valeur ignorée. Dites « reprendre ».') });
      if (etat.mode === 'RECAP') return avec(etat, { message: msg('info', 'Récapitulatif : utilisez les boutons ou « reprendre » pour dicter à nouveau.') });
      // « date douze octobre » : seule façon vocale de changer la date de la ligne (mot-clé explicite)
      const tk = tokensDe(texte);
      if (tk[0] === 'date' && tk.length > 1) {
        const d = interpreterDate(tk.slice(1).join(' '), ctx.periode.annee);
        if (!d.ok) return avec(etat, { message: msg('bad', `Date non appliquée : ${d.raison}`) });
        return appliquer(etat, { type: 'DATE_COURANTE', date: d.date }, ctx);
      }
      const k = etat.cible ?? curseur(ligneC);
      if (k < 0) return avec(etat, { message: msg('warn', 'La ligne est complète : dites « valider », « corriger » ou « annuler la ligne ». Valeur ignorée.') });
      const col = ligneC.cellules[k].colonne;
      let it = interpreterValeur(colonneDe(col).type, texte);
      const confiance = action.confiance;
      if (it.statut === 'RECONNU' && confiance !== undefined && confiance > 0 && confiance < seuil) {
        it = aVerifier(`Reconnaissance peu fiable (${Math.round(confiance * 100)} %) : « ${texte} ». Confirmez ou redites.`, it.valeur);
      }
      const cellule = cellDepuis(col, texte, it, action.source ?? 'VOIX');
      const maj = avecLigne(etat, etat.courante, avecCellule(ligneC, k, cellule));
      const nouveauCurseur = curseur(maj.lignes[maj.courante]);
      let m: Message;
      if (it.statut === 'RECONNU' || it.statut === 'ABSENT') {
        m = nouveauCurseur >= 0 ? msg('info', `${colonneDe(col).libelle} : ${affichageCellule(cellule)}. Suivant : ${colonneDe(maj.lignes[maj.courante].cellules[nouveauCurseur].colonne).libelle}.`) : msg('info', 'Ligne complète : dites « vérifier » puis « validé ».');
      } else {
        m = msg('bad', `${colonneDe(col).libelle} — à vérifier : ${it.message ?? ''}`);
      }
      return { ...maj, cible: it.statut === 'RECONNU' || it.statut === 'ABSENT' ? null : (etat.cible !== null ? k : null), message: m };
    }
  }
}

// ===================================================================================================================
// 6. ENREGISTREMENT : lot à envoyer à la file d'attente existante (tout ou rien)
// ===================================================================================================================
export type PreparationLot = { ok: true; lot: ConsultationInput[] } | { ok: false; raisons: string[] };
/**
 * Re-contrôle TOUT juste avant l'écriture (les données ont pu changer depuis la dictée) : toutes les lignes non vides doivent être
 * validées et PRÊTES ; sinon rien n'est écrit. Le lot est ensuite écrit par importerConsultations (une seule transaction IndexedDB).
 */
export function preparerLot(etat: EtatSaisie, ctx: ContexteSaisie): PreparationLot {
  const raisons: string[] = [];
  const lot: ConsultationInput[] = [];
  etat.lignes.forEach((l, i) => {
    if (ligneVide(l)) return;
    const a = analyserLigne(etat, i, ctx);
    if (!l.validee) raisons.push(`Ligne ${l.position} : non validée.`);
    if (a.etat !== 'PRETE' || !a.consultation) raisons.push(`Ligne ${l.position} : ${a.bloquants[0]?.message ?? (a.aVerifier ? 'valeur(s) à vérifier' : 'colonnes non dictées')}.`);
    else lot.push(a.consultation);
  });
  if (lot.length === 0 && raisons.length === 0) raisons.push('Aucune ligne à enregistrer.');
  return raisons.length ? { ok: false, raisons } : { ok: true, lot };
}

// ===================================================================================================================
// 7. BROUILLON (conservé localement pour ne pas perdre le travail ; jamais d'audio)
// ===================================================================================================================
export function serialiserBrouillon(etat: EtatSaisie): string {
  return JSON.stringify({ ...etat, mode: etat.mode === 'ECOUTE' ? 'PAUSE' : etat.mode, annonce: null, cible: null });
}
/** Relit un brouillon ; toute donnée invalide → null (brouillon ignoré, jamais de plantage). Le micro n'est JAMAIS relancé automatiquement. */
export function lireBrouillon(json: string | null | undefined): EtatSaisie | null {
  if (!json) return null;
  try {
    const o = JSON.parse(json) as EtatSaisie;
    if (!o || o.version !== 1 || !validerOrdre(o.ordre).ok || !Array.isArray(o.lignes) || o.lignes.length === 0) return null;
    for (const l of o.lignes) {
      if (!Array.isArray(l.cellules) || l.cellules.length !== o.ordre.length || !Number.isFinite(l.position)) return null;
      if (!l.cellules.every((c, j) => c && c.colonne === o.ordre[j] && ['ATTENDU', 'RECONNU', 'ABSENT', 'A_VERIFIER', 'ERREUR'].includes(c.statut))) return null;
    }
    const courante = Number.isInteger(o.courante) && o.courante >= 0 && o.courante < o.lignes.length ? o.courante : o.lignes.length - 1;
    return { ...o, courante, cible: null, annonce: null, mode: o.mode === 'RECAP' ? 'RECAP' : 'PAUSE' };
  } catch { return null; }
}
